import { Stack } from '@mui/material';
import { useGetAttendanceCodesQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { GroupsIcon, PageSkeleton, PlusIcon, SettingsIcon } from '@schooly/style';
import React, { FC, PropsWithChildren, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { NoListItemsStub } from '../../components/common/NoListItemsStub/NoListItemsStub';
import MainLayout from '../../components/uikit-components/MainLayout/MainLayout';
import useAppLocation from '../../hooks/useAppLocation';
import useUserCounts from '../../hooks/useUserCounts';
import { AttendanceContent } from './AttendanceContent';
import { AttendanceStats } from './AttendanceStatistics/AttendanceStats';

export const AttendancePage: React.FC = () => {
  const { userCounts } = useUserCounts();

  const { permissions, schoolId = '' } = useAuth();

  const location = useAppLocation();

  const { data: codes, isLoading: isAttendanceCodesLoading } = useGetAttendanceCodesQuery(
    schoolId,
    { enabled: !!schoolId, refetchOnMount: 'always' },
  );

  const isAdmin = permissions.includes('school_admin');

  const { $t } = useIntl();
  const navigate = useNavigate();

  const onConfigurationClick = useCallback(() => {
    navigate('/settings/attendance');
  }, [navigate]);

  const onCreateGroupClick = useCallback(() => {
    navigate('/groups/new');
  }, [navigate]);

  const handleAddButtonClick = useCallback(() => {
    navigate('/attendance/new', {
      state: { backgroundLocation: location },
    });
  }, [location, navigate]);

  const isConfigured = !!codes?.length;

  const renderContent = () => {
    if (isAttendanceCodesLoading) {
      return (
        <AttendancePageSkeleton onCreateRegisterClick={handleAddButtonClick} withHeader>
          <AttendanceStats statistics={[]} loading sx={(theme) => ({ py: theme.spacing(1.25) })} />
        </AttendancePageSkeleton>
      );
    }

    if (!isConfigured) {
      return (
        <NoListItemsStub
          titleText={$t({ id: 'moduleNotConfigured' })}
          type="attendance"
          subTitleText={
            isAdmin
              ? $t({ id: 'school-sections-Attendance-notConfiguredAdmin' })
              : $t({ id: 'school-sections-Attendance-notConfigured' })
          }
          buttonText={isAdmin && $t({ id: 'section-AttendanceSettings' })}
          buttonIcon={isAdmin && <SettingsIcon />}
          onButtonClick={onConfigurationClick}
        />
      );
    }

    if (!userCounts?.group) {
      return (
        <NoListItemsStub
          titleText={$t({ id: 'school-sections-Attendance-ModuleNotAvailableWithoutGroups' })}
          type="attendance"
          subTitleText={$t({ id: 'school-sections-Attendance-noGroups' })}
          buttonText={$t({ id: 'school-sections-Attendance-CreateGroup' })}
          buttonIcon={<GroupsIcon />}
          onButtonClick={onCreateGroupClick}
        />
      );
    }

    return <AttendanceContent codes={codes} handleAddButtonClick={handleAddButtonClick} />;
  };

  return <MainLayout>{renderContent()}</MainLayout>;
};

type AttendancePageSkeletonProps = {
  onCreateRegisterClick: () => void;
  registerCount?: number;
  withHeader?: boolean;
};

export const ATTENDANCE_SKELETON_COLUMNS = [
  { widthPercent: 9, skeletonWidthPercent: 80 },
  { widthPercent: 28, skeletonWidthPercent: 25 },
  { widthPercent: 28, skeletonWidthPercent: 50 },
  { widthPercent: 28, skeletonWidthPercent: 25 },
  { widthPercent: 7, skeletonWidthPercent: 50 },
];

const ROW_CELLS_WITH_BORDER_LEFT = [1];

export const AttendancePageSkeleton: FC<PropsWithChildren<AttendancePageSkeletonProps>> = ({
  onCreateRegisterClick,
  registerCount,
  withHeader = false,
  children,
}) => {
  return (
    <Stack
      sx={{
        [`${ROW_CELLS_WITH_BORDER_LEFT.map((cell) => `& .row-cell-${cell}`).join(', ')}`]: {
          borderLeft: (theme) => `${theme.mixins.borderValue()} !important`,
        },
      }}
    >
      <PageSkeleton
        pageTitleTextId="section-Attendance"
        buttonTextId="attendance-NewRegister"
        buttonIcon={<PlusIcon />}
        onButtonClick={onCreateRegisterClick}
        showActionButton
        pageTitleCounter={registerCount || undefined}
        pageSize={14}
        columns={ATTENDANCE_SKELETON_COLUMNS}
        withHeader={withHeader}
        withSearch={false}
      >
        {children}
      </PageSkeleton>
    </Stack>
  );
};
